import { useState, useEffect } from "react";
import { toast } from "sonner";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import {
  FolderOpen,
  File,
  ChevronRight,
  ChevronLeft,
  ExternalLink,
  RefreshCw,
  Upload,
  Link2,
  HardDrive,
  Plus,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Parse project folder name to extract details
const parseProjectFolder = (folderName, customerName) => {
  // Expected format: 70243_Daizys_OrangeCream_12Slk_8_G_V3
  // or: 01849_LiquidIce_GlacierBerry_12Slk_8G_CanPack
  const parts = folderName.split("_");
  
  let jobNumber = "";
  let productName = "";
  let details = "";
  
  if (parts.length >= 3) {
    jobNumber = parts[0]; // 70243
    // Skip customer name part, get product name
    const customerIndex = parts.findIndex(p => 
      p.toLowerCase().replace(/\s/g, '') === customerName.toLowerCase().replace(/\s/g, '')
    );
    if (customerIndex > 0) {
      productName = parts[customerIndex + 1] || "";
      details = parts.slice(customerIndex + 2).join("_");
    } else {
      productName = parts[2] || "";
      details = parts.slice(3).join("_");
    }
  }
  
  return {
    jobNumber,
    productName,
    details,
    fullName: folderName
  };
};

// Dropdown options from Design Order Form
const CAN_SIZES = ["8oz Sleek", "12oz Sleek", "12oz Standard", "16oz", "19.2oz", "24oz"];
const NECK_ENDS = ["200", "202", "206", "209"];
const DESIGN_TYPES = ["New Design", "Revision", "Reprint", "Color Match"];
const BEVERAGE_TYPES = ["Beer", "Hard Seltzer", "Energy Drink", "Soda", "Water", "Juice", "Wine", "RTD Cocktail", "Other"];
const NUM_COLORS = ["1", "2", "3", "4", "5", "6", "7", "8"];
const APPLICATION_TYPES = ["Shrink Sleeve", "Pressure Sensitive Label", "Direct Print"];
const OVERVARNISH_TYPES = ["Gloss", "Matte", "Satin", "Soft Touch"];
const OVERVARNISH_CONTENTS = ["Full Coverage", "Spot Varnish", "None"];
const INSIDE_VARNISH_CONTENTS = ["Standard", "BPA-NI", "Epoxy", "None"];
const US_STATES = ["AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY"];

const CreateProjectModal = ({ folderInfo, customerName, manufacturer, sharepointUrl, fullPath, onClose, onSuccess }) => {
  const parsed = parseProjectFolder(folderInfo.name, customerName);
  const [customers, setCustomers] = useState([]);
  const [manufacturers, setManufacturers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("general"); // general, design, varnish, additional, contacts
  const [form, setForm] = useState({
    name: `${parsed.jobNumber} - ${parsed.productName || folderInfo.name}`.replace(/_/g, " "),
    customer_name: customerName,
    customer_id: "",
    description: `Product: ${parsed.productName || "N/A"}\nDetails: ${parsed.details || "N/A"}\nJob #: ${parsed.jobNumber}`,
    project_type: "external",
    priority: "medium",
    manufacturer_name: manufacturer || "",
    manufacturer_id: "",
    sharepoint_url: sharepointUrl,
    sharepoint_folder: folderInfo.name,
    full_path: fullPath,
    // Design Order Form fields
    copacker: "",
    brand: "",
    can_size: "",
    neck_end: "",
    design_type: "",
    beverage_type: "",
    number_of_colors: "",
    application_type: "",
    barcode: "",
    gamer_po: parsed.jobNumber || "",
    previous_mfg_design_code: "",
    customer_ref_type: "new",
    // Varnish fields
    overvarnish_type: "",
    overvarnish_content: "",
    inside_varnish_content: "",
    // Additional info
    pilot_requested: false,
    reference_collateral_sent: false,
    requires_pasteurization: false,
    pasteurization_temp: "180F",
    requires_base_coating: false,
    requires_specialty_inks: false,
    // Art contact info
    art_contact_name: "",
    art_contact_phone: "",
    art_contact_address: "",
    art_contact_city: "",
    art_contact_state: "",
    art_contact_zip: "",
    comments: "",
  });

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    try {
      const [customersRes, manufacturersRes] = await Promise.all([
        axios.get(`${API}/customers`),
        axios.get(`${API}/manufacturers`)
      ]);
      
      setCustomers(customersRes.data);
      setManufacturers(manufacturersRes.data);
      
      // Try to find matching customer
      const customerMatch = customersRes.data.find(
        (c) => c.name.toLowerCase().replace(/\s/g, '') === customerName.toLowerCase().replace(/\s/g, '')
      );
      if (customerMatch) {
        setForm((f) => ({ ...f, customer_id: customerMatch.id }));
      }
      
      // Try to find matching manufacturer
      if (manufacturer) {
        const mfgMatch = manufacturersRes.data.find(
          (m) => m.name.toLowerCase().replace(/\s/g, '') === manufacturer.toLowerCase().replace(/\s/g, '')
        );
        if (mfgMatch) {
          setForm((f) => ({ ...f, manufacturer_id: mfgMatch.id }));
        }
      }
    } catch (err) {
      console.error("Failed to fetch data");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.customer_id) {
      toast.error("Please select or create a customer first");
      return;
    }
    
    setLoading(true);
    try {
      const projectData = {
        name: form.name,
        customer_id: form.customer_id,
        description: form.description,
        project_type: form.project_type,
        priority: form.priority,
        manufacturer_id: form.manufacturer_id || null,
        // Design Order Form fields
        copacker: form.copacker || null,
        brand: form.brand || null,
        can_size: form.can_size || null,
        neck_end: form.neck_end || null,
        design_type: form.design_type || null,
        beverage_type: form.beverage_type || null,
        number_of_colors: form.number_of_colors || null,
        application_type: form.application_type || null,
        barcode: form.barcode || null,
        gamer_po: form.gamer_po || null,
        previous_mfg_design_code: form.previous_mfg_design_code || null,
        customer_ref_type: form.customer_ref_type || null,
        // Varnish fields
        overvarnish_type: form.overvarnish_type || null,
        overvarnish_content: form.overvarnish_content || null,
        inside_varnish_content: form.inside_varnish_content || null,
        // Additional info
        pilot_requested: form.pilot_requested,
        reference_collateral_sent: form.reference_collateral_sent,
        requires_pasteurization: form.requires_pasteurization,
        pasteurization_temp: form.requires_pasteurization ? form.pasteurization_temp : null,
        requires_base_coating: form.requires_base_coating,
        requires_specialty_inks: form.requires_specialty_inks,
        // Art contact info
        art_contact_name: form.art_contact_name || null,
        art_contact_phone: form.art_contact_phone || null,
        art_contact_address: form.art_contact_address || null,
        art_contact_city: form.art_contact_city || null,
        art_contact_state: form.art_contact_state || null,
        art_contact_zip: form.art_contact_zip || null,
        comments: form.comments || null,
      };
      
      const res = await axios.post(`${API}/projects`, projectData);
      const projectId = res.data.id;
      
      // Fetch actual files from the SharePoint folder
      const folderPath = fullPath || `${customerName}/${folderInfo.name}`;
      console.log("Fetching files from SharePoint path:", folderPath);
      try {
        const filesRes = await axios.get(`${API}/sharepoint/files`, {
          params: { path: folderPath }
        });
        
        const spFiles = filesRes.data.files || [];
        
        // Add each file to the project with subfolder info
        for (const spFile of spFiles) {
          const ext = spFile.name.split('.').pop().toUpperCase();
          const fileType = ['AI', 'PSD', 'PDF', 'INDD', 'PNG', 'JPG', 'JPEG'].includes(ext) ? ext : 'OTHER';
          
          // Include subfolder in notes (Source, Working File, etc.)
          const subfolderLabel = spFile.subfolder || 'Root';
          
          await axios.post(`${API}/projects/${projectId}/files`, {
            filename: spFile.name,
            file_type: fileType === 'JPEG' ? 'JPG' : fileType,
            file_size: spFile.size ? formatFileSize(spFile.size) : null,
            file_url: spFile.web_url || spFile.download_url,
            notes: `[${subfolderLabel}]`,  // Just the subfolder name as the note
          });
        }
        
        if (spFiles.length === 0) {
          // No files found, add folder link as fallback
          await axios.post(`${API}/projects/${projectId}/files`, {
            filename: `SharePoint Folder: ${form.sharepoint_folder}`,
            file_type: "FOLDER",
            file_url: form.sharepoint_url,
            notes: "Linked from SharePoint browser (no files in folder)",
          });
        }
      } catch (fileErr) {
        console.error("Failed to fetch SharePoint files:", fileErr);
        // Fallback: add folder link if file fetch fails
        await axios.post(`${API}/projects/${projectId}/files`, {
          filename: `SharePoint Folder: ${form.sharepoint_folder}`,
          file_type: "FOLDER",
          file_url: form.sharepoint_url,
          notes: "Linked from SharePoint browser",
        });
      }
      
      toast.success("Project created from SharePoint folder!");
      onSuccess(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create project");
    } finally {
      setLoading(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return null;
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
  };

  const createCustomer = async () => {
    try {
      const res = await axios.post(`${API}/customers`, { name: customerName });
      toast.success(`Customer "${customerName}" created`);
      setCustomers([...customers, res.data]);
      setForm((f) => ({ ...f, customer_id: res.data.id }));
    } catch (err) {
      toast.error("Failed to create customer");
    }
  };

  const createManufacturer = async () => {
    try {
      const res = await axios.post(`${API}/manufacturers`, { name: form.manufacturer_name });
      toast.success(`Manufacturer "${form.manufacturer_name}" created`);
      setManufacturers([...manufacturers, res.data]);
      setForm((f) => ({ ...f, manufacturer_id: res.data.id }));
    } catch (err) {
      toast.error("Failed to create manufacturer");
    }
  };

  const matchingCustomer = customers.find((c) => c.id === form.customer_id);
  const matchingManufacturer = manufacturers.find((m) => m.id === form.manufacturer_id);

  const tabs = [
    { id: "general", label: "General" },
    { id: "design", label: "Design Info" },
    { id: "varnish", label: "Varnish" },
    { id: "additional", label: "Additional" },
    { id: "contacts", label: "Art Contacts" },
  ];

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content max-h-[90vh] w-full max-w-2xl overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 border-b border-border sticky top-0 bg-[#18181B] z-10">
          <h2 className="text-xl font-bold">Create Project from SharePoint</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Auto-populated from folder: {folderInfo.name}
          </p>
          {/* Tabs */}
          <div className="flex gap-1 mt-4 overflow-x-auto pb-1">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`px-3 py-1.5 text-sm rounded-lg whitespace-nowrap transition-colors ${
                  activeTab === tab.id
                    ? "bg-primary text-primary-foreground"
                    : "bg-secondary/50 text-muted-foreground hover:bg-secondary"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          
          {/* General Tab */}
          {activeTab === "general" && (
            <>
              <div>
                <label className="block text-sm font-medium mb-2">Project Name *</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full px-4 py-2.5 bg-background border border-border rounded-lg focus:outline-none focus:ring-1 focus:ring-ring"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Customer *</label>
                {matchingCustomer ? (
                  <div className="flex items-center gap-2">
                    <span className="flex-1 px-4 py-2.5 bg-emerald-950/30 border border-emerald-800 rounded-lg text-emerald-400">
                      ✓ {matchingCustomer.name}
                    </span>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <select
                      value={form.customer_id}
                      onChange={(e) => setForm({ ...form, customer_id: e.target.value })}
                      className="w-full px-4 py-2.5 bg-background border border-border rounded-lg focus:outline-none focus:ring-1 focus:ring-ring"
                    >
                      <option value="">Select existing customer...</option>
                      {customers.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={createCustomer}
                      className="w-full py-2 bg-secondary text-secondary-foreground rounded-lg text-sm hover:bg-secondary/80"
                    >
                      + Create "{customerName}" as new customer
                    </button>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Manufacturer</label>
                {matchingManufacturer ? (
                  <div className="flex items-center gap-2">
                    <span className="flex-1 px-4 py-2.5 bg-amber-950/30 border border-amber-800 rounded-lg text-amber-400">
                      ✓ {matchingManufacturer.name}
                    </span>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <select
                      value={form.manufacturer_id}
                      onChange={(e) => setForm({ ...form, manufacturer_id: e.target.value })}
                      className="w-full px-4 py-2.5 bg-background border border-border rounded-lg focus:outline-none focus:ring-1 focus:ring-ring"
                    >
                      <option value="">Select existing manufacturer...</option>
                      {manufacturers.map((m) => (
                        <option key={m.id} value={m.id}>
                          {m.name}
                        </option>
                      ))}
                    </select>
                    {form.manufacturer_name && (
                      <button
                        type="button"
                        onClick={createManufacturer}
                        className="w-full py-2 bg-amber-950/30 border border-amber-800 text-amber-400 rounded-lg text-sm hover:bg-amber-950/50"
                      >
                        + Create "{form.manufacturer_name}" as new manufacturer
                      </button>
                    )}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Description</label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="w-full px-4 py-2.5 bg-background border border-border rounded-lg focus:outline-none focus:ring-1 focus:ring-ring font-mono text-sm"
                  rows={3}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Type</label>
                  <select
                    value={form.project_type}
                    onChange={(e) => setForm({ ...form, project_type: e.target.value })}
                    className="w-full px-4 py-2.5 bg-background border border-border rounded-lg"
                  >
                    <option value="external">External</option>
                    <option value="internal">Internal</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Priority</label>
                  <select
                    value={form.priority}
                    onChange={(e) => setForm({ ...form, priority: e.target.value })}
                    className="w-full px-4 py-2.5 bg-background border border-border rounded-lg"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>
              </div>

              <div className="p-3 bg-secondary/50 rounded-lg">
                <p className="text-xs text-muted-foreground mb-1">SharePoint Link:</p>
                <a
                  href={form.sharepoint_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-blue-400 hover:underline flex items-center gap-1"
                >
                  <ExternalLink size={12} />
                  {form.sharepoint_folder}
                </a>
              </div>
            </>
          )}

          {/* Design Info Tab */}
          {activeTab === "design" && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Copacker</label>
                  <input
                    type="text"
                    value={form.copacker}
                    onChange={(e) => setForm({ ...form, copacker: e.target.value })}
                    className="w-full px-4 py-2.5 bg-background border border-border rounded-lg focus:outline-none focus:ring-1 focus:ring-ring"
                    placeholder="Enter copacker name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Brand</label>
                  <input
                    type="text"
                    value={form.brand}
                    onChange={(e) => setForm({ ...form, brand: e.target.value })}
                    className="w-full px-4 py-2.5 bg-background border border-border rounded-lg focus:outline-none focus:ring-1 focus:ring-ring"
                    placeholder="Enter brand name"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Can Size</label>
                  <select
                    value={form.can_size}
                    onChange={(e) => setForm({ ...form, can_size: e.target.value })}
                    className="w-full px-4 py-2.5 bg-background border border-border rounded-lg"
                  >
                    <option value="">Select can size...</option>
                    {CAN_SIZES.map((size) => (
                      <option key={size} value={size}>{size}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Neck End</label>
                  <select
                    value={form.neck_end}
                    onChange={(e) => setForm({ ...form, neck_end: e.target.value })}
                    className="w-full px-4 py-2.5 bg-background border border-border rounded-lg"
                  >
                    <option value="">Select neck end...</option>
                    {NECK_ENDS.map((end) => (
                      <option key={end} value={end}>{end}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Design Type</label>
                  <select
                    value={form.design_type}
                    onChange={(e) => setForm({ ...form, design_type: e.target.value })}
                    className="w-full px-4 py-2.5 bg-background border border-border rounded-lg"
                  >
                    <option value="">Select design type...</option>
                    {DESIGN_TYPES.map((type) => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Beverage Type</label>
                  <select
                    value={form.beverage_type}
                    onChange={(e) => setForm({ ...form, beverage_type: e.target.value })}
                    className="w-full px-4 py-2.5 bg-background border border-border rounded-lg"
                  >
                    <option value="">Select beverage type...</option>
                    {BEVERAGE_TYPES.map((type) => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Number of Colors</label>
                  <select
                    value={form.number_of_colors}
                    onChange={(e) => setForm({ ...form, number_of_colors: e.target.value })}
                    className="w-full px-4 py-2.5 bg-background border border-border rounded-lg"
                  >
                    <option value="">Select...</option>
                    {NUM_COLORS.map((num) => (
                      <option key={num} value={num}>{num}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Application Type</label>
                  <select
                    value={form.application_type}
                    onChange={(e) => setForm({ ...form, application_type: e.target.value })}
                    className="w-full px-4 py-2.5 bg-background border border-border rounded-lg"
                  >
                    <option value="">Select application type...</option>
                    {APPLICATION_TYPES.map((type) => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Barcode</label>
                  <input
                    type="text"
                    value={form.barcode}
                    onChange={(e) => setForm({ ...form, barcode: e.target.value })}
                    className="w-full px-4 py-2.5 bg-background border border-border rounded-lg focus:outline-none focus:ring-1 focus:ring-ring"
                    placeholder="Enter barcode"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Gamer P/O</label>
                  <input
                    type="text"
                    value={form.gamer_po}
                    onChange={(e) => setForm({ ...form, gamer_po: e.target.value })}
                    className="w-full px-4 py-2.5 bg-background border border-border rounded-lg focus:outline-none focus:ring-1 focus:ring-ring"
                    placeholder="Enter PO number"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Previous MFG Design Code</label>
                  <input
                    type="text"
                    value={form.previous_mfg_design_code}
                    onChange={(e) => setForm({ ...form, previous_mfg_design_code: e.target.value })}
                    className="w-full px-4 py-2.5 bg-background border border-border rounded-lg focus:outline-none focus:ring-1 focus:ring-ring"
                    placeholder="Index/Code"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Customer Ref No Type</label>
                  <select
                    value={form.customer_ref_type}
                    onChange={(e) => setForm({ ...form, customer_ref_type: e.target.value })}
                    className="w-full px-4 py-2.5 bg-background border border-border rounded-lg"
                  >
                    <option value="new">New</option>
                    <option value="previous">Previous</option>
                  </select>
                </div>
              </div>
            </>
          )}

          {/* Varnish Tab */}
          {activeTab === "varnish" && (
            <>
              <div className="p-4 bg-secondary/30 rounded-lg mb-4">
                <h3 className="font-medium mb-2">External Varnish</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Overvarnish Type</label>
                    <select
                      value={form.overvarnish_type}
                      onChange={(e) => setForm({ ...form, overvarnish_type: e.target.value })}
                      className="w-full px-4 py-2.5 bg-background border border-border rounded-lg"
                    >
                      <option value="">Select...</option>
                      {OVERVARNISH_TYPES.map((type) => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Overvarnish Content</label>
                    <select
                      value={form.overvarnish_content}
                      onChange={(e) => setForm({ ...form, overvarnish_content: e.target.value })}
                      className="w-full px-4 py-2.5 bg-background border border-border rounded-lg"
                    >
                      <option value="">Select...</option>
                      {OVERVARNISH_CONTENTS.map((content) => (
                        <option key={content} value={content}>{content}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <div className="p-4 bg-secondary/30 rounded-lg">
                <h3 className="font-medium mb-2">Internal Varnish</h3>
                <div>
                  <label className="block text-sm font-medium mb-2">Inside Varnish Content</label>
                  <select
                    value={form.inside_varnish_content}
                    onChange={(e) => setForm({ ...form, inside_varnish_content: e.target.value })}
                    className="w-full px-4 py-2.5 bg-background border border-border rounded-lg"
                  >
                    <option value="">Select...</option>
                    {INSIDE_VARNISH_CONTENTS.map((content) => (
                      <option key={content} value={content}>{content}</option>
                    ))}
                  </select>
                </div>
              </div>
            </>
          )}

          {/* Additional Info Tab */}
          {activeTab === "additional" && (
            <>
              <div className="space-y-4">
                <label className="flex items-center gap-3 p-3 bg-secondary/30 rounded-lg cursor-pointer hover:bg-secondary/50 transition-colors">
                  <input
                    type="checkbox"
                    checked={form.pilot_requested}
                    onChange={(e) => setForm({ ...form, pilot_requested: e.target.checked })}
                    className="w-5 h-5 rounded border-border"
                  />
                  <div>
                    <span className="font-medium">Pilot Requested</span>
                    <p className="text-xs text-muted-foreground">Customer would like to have a pilot made</p>
                  </div>
                </label>

                <label className="flex items-center gap-3 p-3 bg-secondary/30 rounded-lg cursor-pointer hover:bg-secondary/50 transition-colors">
                  <input
                    type="checkbox"
                    checked={form.reference_collateral_sent}
                    onChange={(e) => setForm({ ...form, reference_collateral_sent: e.target.checked })}
                    className="w-5 h-5 rounded border-border"
                  />
                  <div>
                    <span className="font-medium">Reference Collateral Sent</span>
                    <p className="text-xs text-muted-foreground">Labels or master cans sent for color matching</p>
                  </div>
                </label>

                <div className="p-3 bg-secondary/30 rounded-lg">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.requires_pasteurization}
                      onChange={(e) => setForm({ ...form, requires_pasteurization: e.target.checked })}
                      className="w-5 h-5 rounded border-border"
                    />
                    <span className="font-medium">Requires Pasteurization</span>
                  </label>
                  {form.requires_pasteurization && (
                    <div className="mt-3 ml-8">
                      <label className="block text-sm font-medium mb-2">Temperature</label>
                      <input
                        type="text"
                        value={form.pasteurization_temp}
                        onChange={(e) => setForm({ ...form, pasteurization_temp: e.target.value })}
                        className="w-32 px-4 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-1 focus:ring-ring"
                        placeholder="180F"
                      />
                    </div>
                  )}
                </div>

                <label className="flex items-center gap-3 p-3 bg-secondary/30 rounded-lg cursor-pointer hover:bg-secondary/50 transition-colors">
                  <input
                    type="checkbox"
                    checked={form.requires_base_coating}
                    onChange={(e) => setForm({ ...form, requires_base_coating: e.target.checked })}
                    className="w-5 h-5 rounded border-border"
                  />
                  <span className="font-medium">Requires Base Coating</span>
                </label>

                <label className="flex items-center gap-3 p-3 bg-secondary/30 rounded-lg cursor-pointer hover:bg-secondary/50 transition-colors">
                  <input
                    type="checkbox"
                    checked={form.requires_specialty_inks}
                    onChange={(e) => setForm({ ...form, requires_specialty_inks: e.target.checked })}
                    className="w-5 h-5 rounded border-border"
                  />
                  <span className="font-medium">Requires Specialty Inks/Coatings</span>
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Comments / Remarks</label>
                <textarea
                  value={form.comments}
                  onChange={(e) => setForm({ ...form, comments: e.target.value })}
                  className="w-full px-4 py-2.5 bg-background border border-border rounded-lg focus:outline-none focus:ring-1 focus:ring-ring"
                  rows={4}
                  placeholder="Any additional notes or instructions..."
                />
              </div>
            </>
          )}

          {/* Art Contacts Tab */}
          {activeTab === "contacts" && (
            <>
              <p className="text-sm text-muted-foreground mb-4">
                Person responsible for approving art related items
              </p>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Name</label>
                  <input
                    type="text"
                    value={form.art_contact_name}
                    onChange={(e) => setForm({ ...form, art_contact_name: e.target.value })}
                    className="w-full px-4 py-2.5 bg-background border border-border rounded-lg focus:outline-none focus:ring-1 focus:ring-ring"
                    placeholder="Contact name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Phone</label>
                  <input
                    type="tel"
                    value={form.art_contact_phone}
                    onChange={(e) => setForm({ ...form, art_contact_phone: e.target.value })}
                    className="w-full px-4 py-2.5 bg-background border border-border rounded-lg focus:outline-none focus:ring-1 focus:ring-ring"
                    placeholder="Phone number"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Address</label>
                <input
                  type="text"
                  value={form.art_contact_address}
                  onChange={(e) => setForm({ ...form, art_contact_address: e.target.value })}
                  className="w-full px-4 py-2.5 bg-background border border-border rounded-lg focus:outline-none focus:ring-1 focus:ring-ring"
                  placeholder="Street address"
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">City</label>
                  <input
                    type="text"
                    value={form.art_contact_city}
                    onChange={(e) => setForm({ ...form, art_contact_city: e.target.value })}
                    className="w-full px-4 py-2.5 bg-background border border-border rounded-lg focus:outline-none focus:ring-1 focus:ring-ring"
                    placeholder="City"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">State</label>
                  <select
                    value={form.art_contact_state}
                    onChange={(e) => setForm({ ...form, art_contact_state: e.target.value })}
                    className="w-full px-4 py-2.5 bg-background border border-border rounded-lg"
                  >
                    <option value="">Select...</option>
                    {US_STATES.map((state) => (
                      <option key={state} value={state}>{state}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">ZIP</label>
                  <input
                    type="text"
                    value={form.art_contact_zip}
                    onChange={(e) => setForm({ ...form, art_contact_zip: e.target.value })}
                    className="w-full px-4 py-2.5 bg-background border border-border rounded-lg focus:outline-none focus:ring-1 focus:ring-ring"
                    placeholder="ZIP code"
                  />
                </div>
              </div>
            </>
          )}

          <div className="flex gap-3 pt-4 border-t border-border mt-6">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2.5 bg-secondary text-secondary-foreground rounded-lg font-medium hover:bg-secondary/80"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !form.customer_id}
              className="flex-1 py-2.5 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 disabled:opacity-50"
            >
              {loading ? "Creating..." : "Create Project"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export const SharePointPage = () => {
  const [status, setStatus] = useState(null);
  const [items, setItems] = useState([]);
  const [currentPath, setCurrentPath] = useState("");
  const [pathHistory, setPathHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createModal, setCreateModal] = useState(null); // { folder, customerName, url }
  const { isAdmin } = useAuth();

  useEffect(() => {
    checkStatus();
  }, []);

  useEffect(() => {
    if (status?.connected) {
      browsePath(currentPath);
    }
  }, [status, currentPath]);

  const checkStatus = async () => {
    try {
      const res = await axios.get(`${API}/sharepoint/status`);
      setStatus(res.data);
    } catch (err) {
      toast.error("Failed to check SharePoint status");
      setStatus({ connected: false });
    } finally {
      setLoading(false);
    }
  };

  const browsePath = async (path) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/sharepoint/browse`, {
        params: { path },
      });
      setItems(res.data.items || []);
    } catch (err) {
      toast.error("Failed to browse SharePoint");
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  const navigateToFolder = (folderName) => {
    setPathHistory([...pathHistory, currentPath]);
    setCurrentPath(currentPath ? `${currentPath}/${folderName}` : folderName);
  };

  const navigateBack = () => {
    if (pathHistory.length > 0) {
      const newHistory = [...pathHistory];
      const previousPath = newHistory.pop();
      setPathHistory(newHistory);
      setCurrentPath(previousPath);
    }
  };

  const navigateToRoot = () => {
    setPathHistory([]);
    setCurrentPath("");
  };

  const formatSize = (bytes) => {
    if (!bytes || bytes === 0) return "-";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString();
  };

  // Get customer name from current path (first folder in path)
  const getCustomerFromPath = () => {
    if (!currentPath) return null;
    return currentPath.split("/")[0];
  };

  // Check if we're at a depth where we can create a project (inside a customer folder)
  const canCreateProject = () => {
    const parts = currentPath.split("/");
    return parts.length >= 1 && currentPath !== ""; // At least inside a customer folder
  };

  const handleCreateProject = (folder) => {
    const customerName = getCustomerFromPath();
    if (!customerName) {
      toast.error("Please navigate into a customer folder first");
      return;
    }
    
    // Detect manufacturer from path (folder between customer and project)
    // Path structure: Customer/Manufacturer/Project or Customer/Project
    const pathParts = currentPath.split("/");
    let manufacturer = null;
    if (pathParts.length >= 2) {
      // If we're at depth 2+, the second folder is the manufacturer
      manufacturer = pathParts[1];
    }
    
    // Build full path to the folder
    const fullFolderPath = currentPath ? `${currentPath}/${folder.name}` : folder.name;
    setCreateModal({
      folder,
      customerName,
      manufacturer,
      url: folder.web_url,
      fullPath: fullFolderPath,
    });
  };

  const pathParts = currentPath ? currentPath.split("/") : [];

  if (loading && !status) {
    return <div className="animate-pulse text-muted-foreground">Connecting to SharePoint...</div>;
  }

  return (
    <div className="animate-fade-in">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold">SharePoint Files</h1>
          <p className="text-muted-foreground mt-1">Browse artwork files in SharePoint</p>
        </div>
        <div className="flex items-center gap-3">
          <div
            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm ${
              status?.connected
                ? "bg-emerald-950/30 text-emerald-400 border border-emerald-800"
                : "bg-red-950/30 text-red-400 border border-red-800"
            }`}
          >
            <HardDrive size={16} />
            {status?.connected ? "Connected" : "Disconnected"}
          </div>
          <button
            onClick={() => {
              checkStatus();
              if (status?.connected) browsePath(currentPath);
            }}
            className="p-2 hover:bg-secondary rounded-lg transition-colors"
            title="Refresh"
          >
            <RefreshCw size={18} />
          </button>
        </div>
      </div>

      {!status?.connected ? (
        <div className="bg-card border border-border rounded-xl p-8 text-center">
          <HardDrive size={48} className="mx-auto mb-4 text-muted-foreground" />
          <h3 className="text-lg font-semibold mb-2">SharePoint Not Connected</h3>
          <p className="text-muted-foreground mb-4">
            Unable to connect to SharePoint. Please check your credentials.
          </p>
          <p className="text-sm text-muted-foreground">
            Base folder: {status?.base_folder || "Not configured"}
          </p>
        </div>
      ) : (
        <>
          {/* Breadcrumb Navigation */}
          <div className="flex items-center gap-2 mb-4 p-3 bg-card border border-border rounded-lg overflow-x-auto">
            <button
              onClick={navigateToRoot}
              className="flex items-center gap-1 text-sm hover:text-foreground text-muted-foreground whitespace-nowrap"
            >
              <FolderOpen size={16} />
              Artwork Files
            </button>
            {pathParts.map((part, index) => (
              <span key={index} className="flex items-center gap-1">
                <ChevronRight size={14} className="text-muted-foreground" />
                <button
                  onClick={() => {
                    const newPath = pathParts.slice(0, index + 1).join("/");
                    setPathHistory(pathHistory.slice(0, index));
                    setCurrentPath(newPath);
                  }}
                  className="text-sm hover:text-foreground text-muted-foreground whitespace-nowrap"
                >
                  {part}
                </button>
              </span>
            ))}
          </div>

          {/* Navigation Buttons */}
          <div className="flex gap-2 mb-4">
            <button
              onClick={navigateBack}
              disabled={pathHistory.length === 0}
              className="flex items-center gap-2 px-3 py-2 bg-secondary text-secondary-foreground rounded-lg text-sm font-medium hover:bg-secondary/80 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft size={16} />
              Back
            </button>
          </div>

          {/* File/Folder List */}
          <div className="bg-card border border-border rounded-xl overflow-hidden">
            {loading ? (
              <div className="p-8 text-center text-muted-foreground animate-pulse">
                Loading...
              </div>
            ) : items.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground">
                <FolderOpen size={48} className="mx-auto mb-4 opacity-50" />
                <p>This folder is empty</p>
              </div>
            ) : (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Type</th>
                    <th>Size</th>
                    <th>Modified</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {items
                    .sort((a, b) => {
                      // Folders first, then files
                      if (a.type === "folder" && b.type !== "folder") return -1;
                      if (a.type !== "folder" && b.type === "folder") return 1;
                      return a.name.localeCompare(b.name);
                    })
                    .map((item, index) => (
                      <tr key={item.id || index}>
                        <td>
                          <div className="flex items-center gap-3">
                            {item.type === "folder" ? (
                              <FolderOpen size={20} className="text-amber-500" />
                            ) : (
                              <File size={20} className="text-blue-500" />
                            )}
                            {item.type === "folder" ? (
                              <button
                                onClick={() => navigateToFolder(item.name)}
                                className="font-medium hover:underline text-left"
                              >
                                {item.name}
                              </button>
                            ) : (
                              <span className="font-medium">{item.name}</span>
                            )}
                          </div>
                        </td>
                        <td className="text-muted-foreground capitalize">{item.type}</td>
                        <td className="text-muted-foreground mono text-sm">
                          {formatSize(item.size)}
                        </td>
                        <td className="text-muted-foreground text-sm">
                          {formatDate(item.last_modified)}
                        </td>
                        <td>
                          <div className="flex items-center gap-2">
                            {item.type === "folder" && isAdmin && canCreateProject() && (
                              <button
                                onClick={() => handleCreateProject(item)}
                                className="p-2 hover:bg-emerald-950/30 text-emerald-400 rounded-lg transition-colors"
                                title="Create Project from this folder"
                              >
                                <Plus size={16} />
                              </button>
                            )}
                            {item.web_url && (
                              <a
                                href={item.web_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="p-2 hover:bg-secondary rounded-lg transition-colors"
                                title="Open in SharePoint"
                              >
                                <ExternalLink size={16} />
                              </a>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Info Footer */}
          <div className="mt-4 text-sm text-muted-foreground">
            <p>
              📁 Base folder: <code className="bg-secondary px-2 py-0.5 rounded">{status.base_folder}</code>
            </p>
            {canCreateProject() && isAdmin && (
              <p className="mt-2 text-emerald-400">
                💡 Tip: Click the <Plus size={14} className="inline" /> button on a project folder to create a project in the database
              </p>
            )}
          </div>
        </>
      )}

      {/* Create Project Modal */}
      {createModal && (
        <CreateProjectModal
          folderInfo={createModal.folder}
          customerName={createModal.customerName}
          manufacturer={createModal.manufacturer}
          sharepointUrl={createModal.url}
          fullPath={createModal.fullPath}
          onClose={() => setCreateModal(null)}
          onSuccess={(project) => {
            setCreateModal(null);
            toast.success(`Project created! Redirecting...`);
            window.location.href = `/projects/${project.id}`;
          }}
        />
      )}
    </div>
  );
};
